import { useEffect, useId, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Sparkles } from 'lucide-react';
import { useTranslation } from '../../i18n/useTranslation';
import { Alert } from '../atoms/Alert/Alert';
import { Badge } from '../atoms/Badge/Badge';
import type { BadgeTone } from '../atoms/Badge/Badge.types';
import { Button } from '../atoms/Button/Button';
import { Card } from '../atoms/Card/Card';
import { EmptyState } from '../atoms/EmptyState';
import { Inline } from '../atoms/Inline/Inline';
import { LiveRegion } from '../atoms/LiveRegion/LiveRegion';
import { Stack } from '../atoms/Stack/Stack';
import { Heading } from '../atoms/Typography/Heading';
import { Text } from '../atoms/Typography/Text';
import { LoadErrorState } from '../feedback/LoadErrorState/LoadErrorState';
import { AiUsageMeter } from '../aiUsage/AiUsageMeter';
import { AiLimitErrorMessage } from '../aiUsage/AiLimitNotice';
import { CollapsibleSection, PENDING_GIVE_UP_MS, PENDING_POLL_MS, errorCode, errorReference, languageName } from '../lighting/LightEvaluationCard';
import { getAiLimitErrorKind } from '../../services/aiUsageService';
import { invalidateAiUsage, useAiUsage } from '../../hooks/useAiUsage';
import type { LightGroup } from '../../services/lightSurveyService';
import {
  MIN_READINGS_FOR_EVALUATION,
  evaluateFlowSurvey,
  flowEvaluationKey,
  formatRange,
  getFlowEvaluation,
  latestFlowEvaluationKey,
  type CombinedZone,
  type FlowEvaluation,
  type FlowPriority,
  type FlowVerdict,
  type FlowZoneAssessment,
  type FlowZoneStats,
  type FlowZones,
} from '../../services/flowSurveyService';
import '../lighting/Lighting.css';

/** Verdict → Badge tone. The Badge text always carries the meaning. */
export const FLOW_VERDICT_TONE: Readonly<Record<FlowVerdict, BadgeTone>> = {
  adequate: 'success',
  dead_spots: 'warning',
  too_strong: 'warning',
  uneven: 'warning',
  insufficient_data: 'neutral',
};

const ZONE_TONE: Readonly<Record<FlowZoneAssessment, BadgeTone>> = { adequate: 'success', too_weak: 'warning', too_strong: 'warning' };
const PRIORITY_TONE: Readonly<Record<FlowPriority, BadgeTone>> = { high: 'danger', medium: 'warning', low: 'neutral' };

function GroupBadges({ groups }: { groups: readonly LightGroup[] }) {
  const { t } = useTranslation();
  if (groups.length === 0) return <Text size="sm" color="muted">{t('flow.evaluation.noGroup')}</Text>;
  return (
    <Inline gap="1">
      {groups.map((g) => (
        <Badge key={g} tone="info">{t(`flow.evaluation.groupShort.${g}`)}</Badge>
      ))}
    </Inline>
  );
}

/** The computed flow zones (not AI): across the tank and by height, median range, flags and the groups each suits. */
function FlowZonesView({ zones }: { zones: FlowZones }) {
  const { t } = useTranslation();
  const row = (z: FlowZoneStats) => (
    <li key={z.band} className="lighting__evaluation-item">
      <Inline gap="2" align="center" justify="between">
        <Text weight="semibold">{t(`flow.evaluation.bands.${z.band}`)}</Text>
        <GroupBadges groups={z.suits} />
      </Inline>
      <Text size="sm" color="secondary">
        {t('flow.evaluation.zoneValues', { range: formatRange(z.medianMin, z.medianMax), points: z.pointLabels.join(', '), alternating: Math.round(z.alternatingShare * 100) })}
      </Text>
    </li>
  );
  return (
    <CollapsibleSection title={t('flow.evaluation.zonesTitle')} count={zones.horizontal.length + zones.levels.length} testId="flow-zones">
      <Stack gap="3">
        <ul className="lighting__list">{zones.horizontal.map(row)}</ul>
        <ul className="lighting__list">{zones.levels.map(row)}</ul>
        <Text size="sm">
          {t('flow.evaluation.flags', {
            dead: zones.deadSpotLabels.join(', ') || '–',
            blasting: zones.blastingLabels.join(', ') || '–',
            laminar: zones.laminarLabels.join(', ') || '–',
          })}
        </Text>
      </Stack>
    </CollapsibleSection>
  );
}

/** Light and flow per zone: a zone suits a group only when both fit. */
function CombinedZonesView({ zones }: { zones: readonly CombinedZone[] }) {
  const { t } = useTranslation();
  return (
    <CollapsibleSection title={t('flow.evaluation.combinedTitle')} count={zones.length} testId="flow-combined-zones">
      <ul className="lighting__list">
        {zones.map((z) => (
          <li key={z.band} className="lighting__evaluation-item">
            <Inline gap="2" align="center" justify="between">
              <Text weight="semibold">{t(`flow.evaluation.bands.${z.band}`)}</Text>
              <GroupBadges groups={z.suits} />
            </Inline>
            <Text size="sm" color="secondary">{t('flow.evaluation.combinedValues', { par: z.medianPar, range: formatRange(z.flowRange[0], z.flowRange[1]) })}</Text>
          </li>
        ))}
      </ul>
    </CollapsibleSection>
  );
}

/** The evaluation itself: verdict, summary, zones, corals, recommendations. */
export function FlowEvaluationResultView({ evaluation }: { evaluation: FlowEvaluation }) {
  const { t, i18n } = useTranslation();
  const { result } = evaluation;
  const lang = evaluation.language;
  const date = new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(evaluation.createdAt));
  const uiLanguage = (i18n.language ?? 'en').split('-')[0];

  return (
    <Stack gap="6">
      <Stack gap="2">
        <Inline gap="2" align="center">
          <Badge tone={FLOW_VERDICT_TONE[result.verdict]} data-testid="flow-evaluation-verdict">{t(`flow.evaluation.verdict.${result.verdict}`)}</Badge>
          <Badge tone="ai">{t(`lighting.evaluation.confidence.${result.confidence}`)}</Badge>
        </Inline>
        <Text lang={lang} data-testid="flow-evaluation-summary">{result.summary}</Text>
        <Text size="sm" color="muted">
          {t('flow.evaluation.meta', { date, points: result.pointCount, pumps: result.pumpCount, corals: result.coralCount })}
          {` · ${String(t(result.usedPhoto ? 'lighting.evaluation.withPhoto' : 'lighting.evaluation.withoutPhoto'))}`}
          {result.usedLightMap && ` · ${String(t('flow.evaluation.withLightMap'))}`}
        </Text>
        {lang !== uiLanguage && <Alert tone="info" compact live="off">{t('lighting.evaluation.otherLanguage', { language: languageName(lang, i18n.language) })}</Alert>}
      </Stack>

      {result.flowZones && <FlowZonesView zones={result.flowZones} />}
      {result.combinedZones.length > 0 && <CombinedZonesView zones={result.combinedZones} />}

      {result.zones.length > 0 && (
        <CollapsibleSection title={t('flow.evaluation.aiZonesTitle')} count={result.zones.length} testId="flow-evaluation-zones">
          <ul className="lighting__list" lang={lang}>
            {result.zones.map((z, i) => (
              <li key={`${z.zone}-${i}`} className="lighting__evaluation-item">
                <Inline gap="2" align="center">
                  <Text weight="semibold">{z.zone}</Text>
                  <Badge tone={ZONE_TONE[z.assessment]}>{t(`flow.evaluation.zone.${z.assessment}`)}</Badge>
                </Inline>
                {z.suitableGroups.length > 0 && <GroupBadges groups={z.suitableGroups} />}
                {z.detail && <Text size="sm" color="secondary">{z.detail}</Text>}
                {z.pointLabels.length > 0 && <Text size="sm" color="muted">{t('flow.evaluation.readings', { points: z.pointLabels.join(', ') })}</Text>}
              </li>
            ))}
          </ul>
        </CollapsibleSection>
      )}

      {result.corals.length > 0 && (
        <CollapsibleSection title={t('flow.evaluation.coralsTitle')} count={result.corals.length}>
          <ul className="lighting__list" data-testid="flow-evaluation-corals">
            {result.corals.map((c) => (
              <li key={c.coralId} className="lighting__evaluation-item">
                <Inline gap="2" align="center">
                  <Text weight="semibold">{t('lighting.evaluation.coralName', { number: c.coralNumber, name: c.name })}</Text>
                  <Badge tone={c.placement === 'well_placed' ? 'success' : c.placement === 'uncertain' ? 'neutral' : 'warning'}>{t(`flow.evaluation.placement.${c.placement}`)}</Badge>
                </Inline>
                <Text size="sm" color="secondary">{t(`flow.evaluation.condition.${c.condition}`)}</Text>
                {c.comment && <Text size="sm" lang={lang}>{c.comment}</Text>}
              </li>
            ))}
          </ul>
        </CollapsibleSection>
      )}

      {result.recommendations.length > 0 && (
        <Stack gap="3">
          <Heading level={3}>{t('flow.evaluation.recommendationsTitle')}</Heading>
          <ol className="lighting__list" data-testid="flow-evaluation-recommendations">
            {result.recommendations.map((r, i) => (
              <li key={`${r.action}-${i}`} className="lighting__evaluation-item">
                <Inline gap="2" align="center">
                  <Badge tone={PRIORITY_TONE[r.priority]}>{t(`lighting.evaluation.priority.${r.priority}`)}</Badge>
                  <Text size="sm" color="muted">
                    {t(`flow.evaluation.action.${r.action}`)}
                    {r.pumpNumber !== null && ` · P${r.pumpNumber}`}
                  </Text>
                </Inline>
                <Text lang={lang}>{r.text}</Text>
              </li>
            ))}
          </ol>
        </Stack>
      )}
      {result.addPumpWithheld && <Text size="sm" color="secondary" data-testid="flow-evaluation-withheld">{t('flow.evaluation.addPumpWithheld')}</Text>}

      <Text variant="caption" color="muted">{t('flow.evaluation.disclaimer')}</Text>
    </Stack>
  );
}

export interface FlowEvaluationCardProps {
  aquariumId: string;
  surveyId: string;
  /** Readings of the survey: the agent needs MIN_READINGS_FOR_EVALUATION. */
  pointCount: number;
}

/**
 * Flow evaluation agent on the flow map page: the AI judges the overlays at
 * peak and trough, the photo, readings, pumps and the light map's zones — dead
 * spots, blasted corals, corals in the wrong flow, what to change. Same states
 * and background run as the lighting card (LightEvaluationCard).
 */
export function FlowEvaluationCard({ aquariumId, surveyId, pointCount }: FlowEvaluationCardProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const headingId = useId();
  const errorRef = useRef<HTMLDivElement>(null);
  const { exceeded: aiCreditsUsedUp } = useAiUsage();
  const [pending, setPending] = useState<{ previousId: string | null; since: number } | null>(null);
  const [pendingGaveUp, setPendingGaveUp] = useState(false);
  const query = useQuery({
    queryKey: flowEvaluationKey(aquariumId, surveyId),
    queryFn: () => getFlowEvaluation(aquariumId, surveyId),
    enabled: Boolean(aquariumId && surveyId),
    refetchInterval: pending ? PENDING_POLL_MS : false,
  });
  const run = useMutation({
    mutationFn: (force: boolean) => evaluateFlowSurvey(aquariumId, surveyId, force),
    onMutate: () => {
      setPendingGaveUp(false);
      return { previousId: query.data?.id ?? null };
    },
    onSuccess: (evaluation, _force, context) => {
      if (!evaluation) {
        setPending({ previousId: context?.previousId ?? null, since: Date.now() });
        return;
      }
      queryClient.setQueryData(flowEvaluationKey(aquariumId, surveyId), evaluation);
      void queryClient.invalidateQueries({ queryKey: latestFlowEvaluationKey(aquariumId) });
      invalidateAiUsage(queryClient);
    },
  });
  useEffect(() => {
    if (!pending) return;
    const latest = query.data;
    if (latest && latest.id !== pending.previousId) {
      setPending(null);
      void queryClient.invalidateQueries({ queryKey: latestFlowEvaluationKey(aquariumId) });
      invalidateAiUsage(queryClient);
    } else if (Date.now() - pending.since > PENDING_GIVE_UP_MS) {
      setPending(null);
      setPendingGaveUp(true);
    }
  }, [pending, query.data, query.dataUpdatedAt, queryClient, aquariumId]);
  const enoughReadings = pointCount >= MIN_READINGS_FOR_EVALUATION;
  useEffect(() => {
    if (run.isError || pendingGaveUp) errorRef.current?.focus();
  }, [run.isError, pendingGaveUp]);

  const evaluation = query.data ?? null;
  const evaluate = (force: boolean): void => run.mutate(force);
  const busy = run.isPending || pending !== null;
  const disabled = busy || aiCreditsUsedUp || !enoughReadings;

  let runError = null;
  if (pendingGaveUp) {
    runError = <Alert ref={errorRef} tabIndex={-1} compact>{t('flow.evaluation.error')}</Alert>;
  } else if (run.isError) {
    const code = errorCode(run.error);
    runError = getAiLimitErrorKind(run.error) ? (
      <AiLimitErrorMessage ref={errorRef} tabIndex={-1} error={run.error} suppressExceeded={aiCreditsUsedUp} />
    ) : (
      <Alert ref={errorRef} tabIndex={-1} compact>
        {t(code === 'NOT_ENOUGH_READINGS' ? 'flow.evaluation.errorReadings' : code === 'RATE_LIMITED' ? 'flow.evaluation.errorRate' : 'flow.evaluation.error')}
        {code !== 'NOT_ENOUGH_READINGS' && code !== 'RATE_LIMITED' && errorReference(run.error) && (
          <Text as="span" size="sm" className="lighting__error-reference" data-testid="flow-evaluation-error-reference">
            {t('lighting.evaluation.errorReference', { reference: errorReference(run.error) })}
          </Text>
        )}
      </Alert>
    );
  }

  let body;
  if (query.isPending) {
    body = <LiveRegion busy><Text color="muted">{t('flow.evaluation.loading')}</Text></LiveRegion>;
  } else if (query.isError) {
    body = <LoadErrorState title={t('flow.evaluation.loadError')} onRetry={() => void query.refetch()} retryLabel={t('flow.retry')} titleLevel={3} />;
  } else if (!evaluation) {
    body = enoughReadings ? (
      <EmptyState
        icon={<Sparkles size={32} />}
        headline={t('flow.evaluation.emptyTitle')}
        description={t('flow.evaluation.emptyDescription')}
        actionLabel={busy ? undefined : t('flow.evaluation.run')}
        onAction={aiCreditsUsedUp ? undefined : () => evaluate(false)}
        headingLevel={3}
      />
    ) : (
      <EmptyState
        icon={<Sparkles size={32} />}
        headline={t('flow.evaluation.needReadingsTitle')}
        description={t('flow.evaluation.needReadings', { min: MIN_READINGS_FOR_EVALUATION, n: pointCount })}
        headingLevel={3}
      />
    );
  } else {
    body = (
      <Stack gap="4">
        {evaluation.outdated && (
          <Alert tone="info" live="off" compact data-testid="flow-evaluation-outdated">
            {t('flow.evaluation.outdated')}
          </Alert>
        )}
        <FlowEvaluationResultView evaluation={evaluation} />
      </Stack>
    );
  }

  return (
    <Card as="section" aria-labelledby={headingId} data-testid="flow-evaluation-card">
      <Stack gap="4">
        <Inline justify="between" gap="3" align="center">
          <Inline gap="2" align="center" wrap={false}>
            <span className="lighting__evaluation-icon" aria-hidden="true"><Sparkles size={20} /></span>
            <Heading level={3} id={headingId}>{t('flow.evaluation.cardTitle')}</Heading>
          </Inline>
          {evaluation && (
            <Button variant={evaluation.outdated ? 'primary' : 'secondary'} size="sm" onClick={() => evaluate(!evaluation.outdated)} isLoading={busy} disabled={disabled}>
              {t(evaluation.outdated ? 'flow.evaluation.rerunOutdated' : 'flow.evaluation.rerun')}
            </Button>
          )}
        </Inline>
        <Text color="secondary" size="sm">{t('flow.evaluation.intro')}</Text>
        <AiUsageMeter variant="compact" />
        {run.isPending && (
          <LiveRegion busy>
            <Text color="muted">{t('flow.evaluation.running')}</Text>
          </LiveRegion>
        )}
        {pending && (
          <LiveRegion busy>
            <Text color="muted" data-testid="flow-evaluation-pending">{t('flow.evaluation.pending')}</Text>
          </LiveRegion>
        )}
        {runError}
        {body}
      </Stack>
    </Card>
  );
}

export default FlowEvaluationCard;
